import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import styled from "styled-components/macro";
import { EditorContent, ReactRenderer, useEditor } from "@tiptap/react";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import Paragraph from "@tiptap/extension-paragraph";
import Document from "@tiptap/extension-document";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import History from "@tiptap/extension-history";
import { Extension, InputRule, mergeAttributes, type JSONContent } from "@tiptap/core";
import { Plugin, PluginKey, type EditorState } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { MentionSuggestionsContainer } from "src/components/PersistentChat/mentions/MentionSuggestionsContainer";
import { cn } from "src/lib/cn";
import SVG from "react-inlinesvg";
import { Text as SudoText } from "src/sudokit/components/Text";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import usePublicMode from "src/hooks/usePublicMode";
import { variableDescription, variableLabel } from "./AdvancedEditor/utils";
import { useDropdownNavigation } from "./useDropdownNavigation";

export interface VariableOption {
  name: string;
  label: string;
  description: string;
}

const VARIABLE_ICON_MAP: Record<string, string> = {
  prompt_1_result: "AI-sparkle.svg",
  user_text_input: "plugin.svg",
  braindump: "story-bible/seed-icon.svg",
  style: "story-bible/style-icon.svg",
  synopsis: "story-bible/synopsis-icon.svg",
  genre: "story-bible/genre-icon.svg",
  is_story_bible_active: "story-bible/StoryBibleIcon.svg",
  characters: "character-icon.svg",
  characters_raw: "character-icon.svg",
  worldbuilding: "world.svg",
  worldbuilding_raw: "world.svg",
  outline: "story-bible/outline-icon.svg",
  scene_summary: "story-bible/outline-icon.svg",
  chapter_scenes: "story-bible/generator.svg",
  chapter_scenes_extra_instructions: "story-bible/generator.svg",
};

function resolveAssetPath(iconPath: string, isPublicPluginsMode: boolean) {
  const normalizedPath = iconPath.startsWith("/") ? iconPath.slice(1) : iconPath;
  const basePath = isPublicPluginsMode ? "/plugins/assets" : "/assets";
  return `${basePath}/${normalizedPath}`;
}

function getVariableIconPath(variableName: string) {
  if (/^prompt_\d+_result$/.test(variableName)) {
    return "AI-sparkle.svg";
  }

  return VARIABLE_ICON_MAP[variableName] || "document.svg";
}

const TOKEN_PATTERN = /\{\{\s*([^}]+?)\s*\}\}/g;
const CARET_HINT_PLUGIN_KEY = new PluginKey<boolean>("promptVariableCaretHint");

type VariableMentionItem = VariableOption & {
  id: string;
  icon: string;
};

type VariableSuggestionListRef = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

function isSimpleVariableToken(rawToken: string) {
  return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(rawToken.trim());
}

function makeTextNode(text: string): JSONContent[] {
  if (!text) return [];
  return [{ type: "text", text }];
}

function parseLineToContent(line: string, variableLookup: Map<string, VariableMentionItem>): JSONContent[] {
  const content: JSONContent[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  TOKEN_PATTERN.lastIndex = 0;
  while ((match = TOKEN_PATTERN.exec(line)) !== null) {
    if (match.index > lastIndex) {
      content.push(...makeTextNode(line.slice(lastIndex, match.index)));
    }

    const rawTokenBody = (match[1] || "").trim();
    const baseToken = rawTokenBody.split("|")[0].trim();
    const option = variableLookup.get(baseToken);

    if (option && isSimpleVariableToken(rawTokenBody)) {
      content.push({
        type: "variableMention",
        attrs: {
          id: option.name,
          label: option.label,
        },
      });
    } else {
      content.push(...makeTextNode(match[0]));
    }

    lastIndex = TOKEN_PATTERN.lastIndex;
  }

  if (lastIndex < line.length) {
    content.push(...makeTextNode(line.slice(lastIndex)));
  }

  return content;
}

function buildDocFromTemplate(
  value: string,
  variableLookup: Map<string, VariableMentionItem>
): JSONContent {
  const lines = value.split("\n");

  return {
    type: "doc",
    content: lines.map((line) => ({
      type: "paragraph",
      content: parseLineToContent(line, variableLookup),
    })),
  };
}

function serializeInlineNode(node: JSONContent): string {
  if (node.type === "text") {
    return node.text || "";
  }

  if (node.type === "variableMention") {
    const variableName = String(node.attrs?.id || "").trim();
    return variableName ? `{{ ${variableName} }}` : "";
  }

  if (node.type === "hardBreak") {
    return "\n";
  }

  if (Array.isArray(node.content)) {
    return node.content.map(serializeInlineNode).join("");
  }

  return "";
}

function serializeDocToTemplate(doc: JSONContent): string {
  const blocks = Array.isArray(doc.content) ? doc.content : [];
  if (blocks.length === 0) {
    return "";
  }

  return blocks
    .map((block) => {
      if (block.type === "paragraph") {
        return (block.content || []).map(serializeInlineNode).join("");
      }
      return serializeInlineNode(block);
    })
    .join("\n");
}

function parseTemplateTextToInlineContent(
  value: string,
  variableLookup: Map<string, VariableMentionItem>
): JSONContent[] {
  const lines = value.split("\n");
  const content: JSONContent[] = [];

  lines.forEach((line, index) => {
    content.push(...parseLineToContent(line, variableLookup));
    if (index < lines.length - 1) {
      content.push({ type: "hardBreak" });
    }
  });

  return content;
}

function hasVariableMentionNode(content: JSONContent[]): boolean {
  return content.some((node) => node.type === "variableMention");
}

function serializeSliceToTemplate(slice: {
  content: {
    size: number;
    textBetween: (
      from: number,
      to: number,
      blockSeparator?: string,
      leafText?: (node: { type: { name: string }; attrs?: Record<string, unknown>; textContent?: string }) => string
    ) => string;
  };
}) {
  return slice.content.textBetween(
    0,
    slice.content.size,
    "\n",
    (node) => {
      if (node.type.name === "variableMention") {
        const variableName = String(node.attrs?.id ?? "").trim();
        return variableName ? `{{ ${variableName} }}` : "";
      }

      if (node.type.name === "hardBreak") {
        return "\n";
      }

      return node.textContent || "";
    }
  );
}

function serializeDomNodeToTemplate(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || "";
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const element = node as HTMLElement;
  const variableName = element.getAttribute("data-variable-id")?.trim();
  if (variableName) {
    return `{{ ${variableName} }}`;
  }

  if (element.tagName === "BR") {
    return "\n";
  }

  const childText = Array.from(element.childNodes)
    .map(serializeDomNodeToTemplate)
    .join("");
  const shouldAppendParagraphBreak = element.tagName === "P" || element.tagName === "DIV";
  return shouldAppendParagraphBreak ? `${childText}\n` : childText;
}

function serializeDomSelectionToTemplate(selection: Selection | null): string {
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return "";
  }

  const wrapper = document.createElement("div");
  for (let i = 0; i < selection.rangeCount; i += 1) {
    wrapper.appendChild(selection.getRangeAt(i).cloneContents());
  }

  return serializeDomNodeToTemplate(wrapper).replace(/\n+$/, "");
}

function hasRenderableContent(doc: JSONContent): boolean {
  const blocks = Array.isArray(doc.content) ? doc.content : [];

  for (const block of blocks) {
    const inlineNodes = Array.isArray(block.content) ? block.content : [block];
    for (const node of inlineNodes) {
      if (node.type === "variableMention") {
        return true;
      }
      if (node.type === "text" && (node.text || "").trim().length > 0) {
        return true;
      }
    }
  }

  return false;
}

function canShowCaretHintAtSelection(state: EditorState): boolean {
  if (!state.selection.empty) {
    return false;
  }

  const { $from } = state.selection;
  const parent = $from.parent;
  if (parent.type.name !== "paragraph") {
    return false;
  }

  const paragraphText = parent.textContent || "";
  const isBlankParagraph = paragraphText.trim().length === 0;
  const isAtBeginningOfParagraph = $from.parentOffset === 0;

  return isBlankParagraph && isAtBeginningOfParagraph;
}

function positionSuggestionPopup(
  popup: HTMLDivElement,
  clientRect: () => DOMRect | null
) {
  const rect = clientRect();
  if (!rect) return;

  const popupWidth = 352;
  const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
  const padding = 8;
  const left = Math.min(
    Math.max(rect.left, padding),
    viewportWidth - popupWidth - padding
  );

  popup.style.left = `${left}px`;
  popup.style.top = `${rect.bottom + 4}px`;
}

const VariableSuggestionList = forwardRef<
  VariableSuggestionListRef,
  {
    items: VariableMentionItem[];
    query: string;
    command: (item: VariableMentionItem) => void;
  }
>(({ items, query, command }, ref) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const {
    highlightedIndex: selectedIndex,
    setHighlightedIndex: setSelectedIndex,
    resetHighlightIndex,
    handleNavigationKeyDown,
  } = useDropdownNavigation({
    itemCount: items.length,
    listRef,
    optionIndexAttribute: "data-variable-option-index",
    stickyTopSelector: undefined,
  });

  useEffect(() => {
    resetHighlightIndex();
  }, [items, resetHighlightIndex]);

  const selectItem = (index: number) => {
    const item = items[index];
    if (!item) return;
    command(item);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: (event) => {
      return handleNavigationKeyDown(event, {
        onSelectIndex: selectItem,
      });
    },
  }));

  return (
    <MentionSuggestionsContainer>
      <WithReset
        ref={listRef}
        className="flex flex-col overflow-y-auto max-h-[260px] w-[22rem]"
        role="listbox"
        aria-label="Variable autocomplete options"
      >
        {items.length > 0 ? (
          items.map((item, index) => (
            <WithReset
              key={item.name}
              as="button"
              type="button"
              role="option"
              aria-selected={index === selectedIndex}
              data-variable-option-index={index}
              className={cn(
                "sudokit use-component-styles flex items-start gap-2 w-full py-[9.5px] px-[12px] cursor-pointer transition-all text-left text-app-grey-0",
                index === selectedIndex ? "bg-[var(--menu-color-hover)] rounded-xl" : "active:bg-app-menu-color-active active:scale-[0.99]",
              )}
              onMouseEnter={() => setSelectedIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                selectItem(index);
              }}
            >
              <WithReset className="w-6 h-6 flex items-center justify-center shrink-0 self-start mt-0.5">
                <SVG
                  src={item.icon}
                  className="w-5 h-5 [&_path]:fill-app-grey-0"
                />
              </WithReset>
              <WithReset className="min-w-0 flex flex-col gap-0.5 flex-1 text-left">
                <SudoText className="truncate leading-snug">{item.label}</SudoText>
                <SudoText
                  size="xs"
                  color="grey-1"
                  className="leading-tight whitespace-normal break-words"
                >
                  {item.description}
                </SudoText>
              </WithReset>
            </WithReset>
          ))
        ) : (
          <WithReset className="px-[12px] py-[10px]">
            <SudoText
              size="sm"
              color="grey-1"
            >
              {`No variables matching "${query || "#..."}" found.`}
            </SudoText>
          </WithReset>
        )}
      </WithReset>
    </MentionSuggestionsContainer>
  );
});

VariableSuggestionList.displayName = "VariableSuggestionList";

function createSyntheticChangeEvent(
  name: string | undefined,
  value: string
): React.ChangeEvent<HTMLTextAreaElement> {
  return {
    target: { name, value },
    currentTarget: { name, value },
  } as React.ChangeEvent<HTMLTextAreaElement>;
}

function createSyntheticBlurEvent(
  name: string | undefined,
  value: string
): React.FocusEvent<HTMLTextAreaElement> {
  return {
    target: { name, value },
    currentTarget: { name, value },
  } as React.FocusEvent<HTMLTextAreaElement>;
}

const Container = styled.div`
  position: relative;
  width: 100%;

  .prompt-variable-editor {
    width: 100%;
    max-width: 100%;
    min-height: 120px;
    border: 1px solid var(--grey-3);
    border-radius: 5px;
    background: transparent;
    color: var(--grey-0);
    font-family: Inter, system-ui, sans-serif !important;
    font-size: inherit;
    font-weight: inherit;
    line-height: 1.55;
    padding: 0.8rem 12px;
    white-space: pre-wrap;
    word-wrap: break-word;
    word-break: break-word;
    overflow-y: auto;
    outline: none;
    transition:
      border-color 100ms ease-in-out,
      box-shadow 100ms ease-in-out;
  }

  .prompt-variable-editor:focus {
    border-color: var(--primary-cta-high-contrast);
    box-shadow: var(--tiny-shadow, 0 0 0 1px var(--primary-cta-high-contrast));
  }

  .prompt-variable-editor--with-insert-context {
    padding-bottom: 52px;
  }

  .prompt-variable-insert-context-wrapper {
    position: absolute;
    left: 8px;
    bottom: 8px;
    display: inline-flex !important;
    width: 28px !important;
    height: 28px !important;
    pointer-events: none;
  }

  .prompt-variable-insert-context {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    box-sizing: border-box;
    border: 1px solid var(--primary-cta-translucent);
    border-radius: 9px;
    background: var(--primary-cta-50);
    appearance: none;
    color: var(--primary-cta);
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    user-select: none;
    pointer-events: auto;
    transition:
      background-color 120ms ease-in-out,
      border-color 120ms ease-in-out,
      transform 120ms ease-in-out;
  }

  .prompt-variable-insert-context:hover {
    background: var(--primary-cta-100);
    border-color: var(--primary-cta-200);
  }

  .prompt-variable-insert-context:active {
    transform: scale(0.97);
  }

  .prompt-variable-insert-context:focus-visible {
    outline: 2px solid var(--primary-cta-high-contrast);
    outline-offset: 2px;
  }

  .prompt-variable-insert-context[data-disabled="true"] {
    cursor: default;
    opacity: 0.4;
    transform: none;
  }

  .prompt-variable-editor p {
    margin: 0;
  }

  .prompt-variable-editor p + p {
    margin-top: 0.5rem;
  }

  .prompt-variable-editor p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    color: var(--grey-0);
    opacity: 0.5;
    float: left;
    height: 0;
    pointer-events: none;
  }

  .prompt-variable-caret-hint {
    color: var(--grey-0);
    opacity: 0.5;
    font-size: 0.9em;
    font-style: italic;
    pointer-events: none;
    user-select: none;
    white-space: pre-wrap;
  }

  .prompt-variable-mention {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    border-radius: 6px;
    padding: 2px 4px 2px 4px;
    border: 1px solid var(--grey-3);
    font-weight: 500;
    vertical-align: middle;
    cursor: grab;
  }

  .prompt-variable-mention:active {
    cursor: grabbing;
  }

  .prompt-variable-mention--configurable {
    position: relative;
    cursor: pointer;
  }

  .prompt-variable-mention--dragging,
  .prompt-variable-mention--configurable.prompt-variable-mention--dragging {
    cursor: grabbing;
  }

  .prompt-variable-mention--configurable::after {
    content: "Customize";
    position: absolute;
    bottom: calc(100% + 9px);
    left: 50%;
    transform: translateX(-50%);
    background: var(--menu-color-hover);
    color: var(--grey-0);
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 12px;
    line-height: 1.1;
    font-weight: 500;
    white-space: nowrap;
    opacity: 0;
    transition: opacity 120ms ease-in-out;
    pointer-events: none;
    z-index: 4;
  }

  .prompt-variable-mention--configurable:hover::after {
    opacity: 1;
  }

  .prompt-variable-mention--configurable.prompt-variable-mention--remove-hover::after {
    opacity: 0;
  }

  .prompt-variable-mention-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    background-color: var(--grey-0);
    mask: var(--prompt-variable-mention-icon) center / contain no-repeat;
    -webkit-mask: var(--prompt-variable-mention-icon) center / contain no-repeat;
  }

  .prompt-variable-mention-label {
    font-size: 12px;
    color: var(--grey-0);
    line-height: 1.2;
    white-space: nowrap;
  }

  .prompt-variable-mention-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: 10px;
  }

  .prompt-variable-mention-action-icon {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    opacity: 0.72;
    filter: grayscale(1);
  }

  .prompt-variable-mention-edit-icon {
    width: 13px;
    height: 13px;
  }

  .prompt-variable-mention-edit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    transition: background-color 120ms ease-in-out;
  }

  .prompt-variable-mention-edit:hover {
    background: var(--menu-color-hover);
  }

  .prompt-variable-mention-edit:active {
    background: var(--menu-color-active);
  }

  .prompt-variable-mention--configurable:hover .prompt-variable-mention-edit {
    background: var(--menu-color-hover);
  }

  .prompt-variable-mention--configurable.prompt-variable-mention--remove-hover .prompt-variable-mention-edit {
    background: transparent;
  }

  .prompt-variable-mention-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    transition: background-color 120ms ease-in-out;
  }

  .prompt-variable-mention-remove:hover {
    background: var(--menu-color-hover);
  }

  .prompt-variable-mention-remove:active {
    background: var(--menu-color-active);
  }

  .prompt-variable-mention-remove-symbol {
    color: var(--grey-1);
    font-size: 14px;
    line-height: 1;
    font-weight: 700;
  }

  .prompt-variable-mention-indicator {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #f7c948;
    margin-left: 2px;
    flex-shrink: 0;
  }
`;

type BasePromptEditorProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export interface HighlightedPromptTextareaProps extends Omit<BasePromptEditorProps, "value"> {
  validVariables?: string[];
  variableOptions?: VariableOption[];
  value?: string;
  customizableVariables?: string[];
  onVariableMentionClick?: (variableName: string) => void;
  onVariableMentionInsert?: (variableName: string) => void;
  caretHintText?: string;
  indicatorVariables?: string[];
  showInsertProjectContextButton?: boolean;
}

export const HighlightedPromptTextarea: React.FC<HighlightedPromptTextareaProps> = ({
  validVariables = [],
  variableOptions = [],
  customizableVariables = [],
  onVariableMentionClick,
  onVariableMentionInsert,
  caretHintText = "",
  indicatorVariables = [],
  showInsertProjectContextButton = false,
  value = "",
  ...restProps
}) => {
  const { isPublicPluginsMode } = usePublicMode();
  const {
    onChange: passedOnChange,
    onKeyDown: passedOnKeyDown,
    onBlur: passedOnBlur,
    className,
    name,
    id,
    disabled,
    placeholder,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    ...unusedTextareaProps
  } = restProps;

  const validVariablesSignature = validVariables.join("|");
  const variableOptionsSignature = variableOptions.map((option) => `${option.name}:${option.label}:${option.description}`).join("|");
  const customizableVariablesSignature = customizableVariables.join("|");
  const indicatorVariablesSignature = indicatorVariables.join("|");
  const customizableVariableSet = useMemo(() => new Set(customizableVariables), [customizableVariablesSignature]);
  const customizableVariableSetRef = useRef(customizableVariableSet);
  customizableVariableSetRef.current = customizableVariableSet;
  const indicatorVariableSet = useMemo(() => new Set(indicatorVariables), [indicatorVariablesSignature]);
  const indicatorVariableSetRef = useRef(indicatorVariableSet);
  indicatorVariableSetRef.current = indicatorVariableSet;

  const resolvedVariableOptions = useMemo(() => {
    if (variableOptions.length > 0) {
      return variableOptions;
    }
    return validVariables.map((variable) => ({
      name: variable,
      label: variableLabel(variable),
      description: variableDescription(variable),
    }));
  }, [validVariablesSignature, variableOptionsSignature]);

  const mentionItems = useMemo<VariableMentionItem[]>(
    () =>
      resolvedVariableOptions.map((option) => ({
        ...option,
        id: option.name,
        icon: resolveAssetPath(getVariableIconPath(option.name), isPublicPluginsMode),
      })),
    [resolvedVariableOptions, isPublicPluginsMode],
  );

  const variableLookup = useMemo(() => new Map(mentionItems.map((item) => [item.name, item])), [mentionItems]);

  const editorValueRef = useRef(value);
  const editorInstanceRef = useRef<any>(null);
  const nameRef = useRef(name);
  nameRef.current = name;
  const onChangeRef = useRef(passedOnChange);
  onChangeRef.current = passedOnChange;
  const onBlurRef = useRef(passedOnBlur);
  onBlurRef.current = passedOnBlur;
  const onKeyDownRef = useRef(passedOnKeyDown);
  onKeyDownRef.current = passedOnKeyDown;
  const onVariableMentionClickRef = useRef(onVariableMentionClick);
  onVariableMentionClickRef.current = onVariableMentionClick;
  const onVariableMentionInsertRef = useRef(onVariableMentionInsert);
  onVariableMentionInsertRef.current = onVariableMentionInsert;
  const isDraggingVariableRef = useRef(false);
  const draggedVariableElementRef = useRef<HTMLElement | null>(null);
  const pendingMentionClickRef = useRef<{
    variableName: string;
    startX: number;
    startY: number;
  } | null>(null);

  const variableMentionExtension = useMemo(
    () =>
      Mention.extend({
        draggable: true,
        selectable: true,
        name: "variableMention",
        addOptions() {
          const parent = this.parent?.() ?? {};
          return {
            ...parent,
            renderHTML(props: any) {
              const { node, HTMLAttributes } = props;
              const variableName = String(node.attrs.id ?? "");
              const isConfigurable = customizableVariableSet.has(variableName);
              const label = String(node.attrs.label ?? "") || variableLookup.get(variableName)?.label || variableLabel(variableName);
              const icon = variableLookup.get(variableName)?.icon || resolveAssetPath(getVariableIconPath(variableName), isPublicPluginsMode);
              const showIndicator = indicatorVariableSetRef.current.has(variableName);
              const pencilIcon = resolveAssetPath("pencil.svg", isPublicPluginsMode);
              return [
                "span",
                mergeAttributes(
                  {
                    "data-type": "variable-mention",
                    "data-variable-id": variableName,
                    class: cn("prompt-variable-mention", isConfigurable && "prompt-variable-mention--configurable"),
                    contenteditable: "false",
                  },
                  HTMLAttributes,
                ),
                [
                  "span",
                  {
                    "aria-hidden": "true",
                    class: "prompt-variable-mention-icon",
                    style: `--prompt-variable-mention-icon: url("${icon}")`,
                  },
                ],
                ["span", { class: "prompt-variable-mention-label" }, label],
                ...(showIndicator ? [["span", { class: "prompt-variable-mention-indicator", "aria-hidden": "true" }]] : []),
                [
                  "span",
                  {
                    class: "prompt-variable-mention-actions",
                    "aria-hidden": "true",
                  },
                  ...(isConfigurable
                    ? [
                        [
                          "span",
                          {
                            class: "prompt-variable-mention-edit",
                            "aria-hidden": "true",
                          },
                          [
                            "img",
                            {
                              src: pencilIcon,
                              alt: "",
                              class: "prompt-variable-mention-action-icon prompt-variable-mention-edit-icon",
                            },
                          ],
                        ],
                      ]
                    : []),
                  [
                    "span",
                    {
                      class: "prompt-variable-mention-remove",
                      "data-variable-mention-action": "remove",
                      role: "button",
                      "aria-label": "Remove variable",
                      title: "Remove variable",
                    },
                    [
                      "span",
                      {
                        class: "prompt-variable-mention-remove-symbol",
                      },
                      "×",
                    ],
                  ],
                ],
              ];
            },
            renderText({ node }) {
              const variableName = String(node.attrs.id ?? "").trim();
              return variableName ? `{{ ${variableName} }}` : "";
            },
          };
        },
      }).configure({
        HTMLAttributes: {},
        suggestion: {
          char: "#",
          allowSpaces: false,
          items: ({ query }: { query: string }) => {
            const normalizedQuery = query.trim().toLowerCase();
            if (!normalizedQuery) {
              return mentionItems;
            }

            return mentionItems.filter((item) => {
              return item.name.toLowerCase().includes(normalizedQuery) || item.label.toLowerCase().includes(normalizedQuery);
            });
          },
          command: ({ editor, range, props }: any) => {
            const item = props as VariableMentionItem;
            if (!item?.name) return;

            editor
              .chain()
              .focus()
              .insertContentAt(range, {
                type: "variableMention",
                attrs: {
                  id: item.name,
                  label: item.label,
                },
              })
              .insertContent(" ")
              .run();

            if (customizableVariableSetRef.current.has(item.name)) {
              onVariableMentionInsertRef.current?.(item.name);
            }
          },
          render: () => {
            let component: ReactRenderer<VariableSuggestionListRef> | null = null;
            let popup: HTMLDivElement | null = null;

            const destroy = () => {
              component?.destroy();
              component = null;
              popup?.remove();
              popup = null;
            };

            return {
              onStart: (props: any) => {
                if (!props.clientRect) return;

                component = new ReactRenderer(VariableSuggestionList, {
                  props: {
                    items: props.items,
                    query: props.query || "",
                    command: (item: VariableMentionItem) => props.command(item),
                  },
                  editor: props.editor,
                });

                popup = document.createElement("div");
                popup.style.position = "fixed";
                popup.style.zIndex = "10000";
                document.body.appendChild(popup);
                popup.appendChild(component.element);
                positionSuggestionPopup(popup, props.clientRect);
              },
              onUpdate: (props: any) => {
                if (!component) return;
                component.updateProps({
                  items: props.items,
                  query: props.query || "",
                  command: (item: VariableMentionItem) => props.command(item),
                });
                if (popup && props.clientRect) {
                  positionSuggestionPopup(popup, props.clientRect);
                }
              },
              onKeyDown: ({ event }: { event: KeyboardEvent }) => {
                if (event.key === "Escape") {
                  destroy();
                  return true;
                }
                return component?.ref?.onKeyDown(event) ?? false;
              },
              onExit: destroy,
            };
          },
        },
      }),
    [mentionItems, variableLookup, customizableVariableSet, isPublicPluginsMode],
  );

  const caretHintExtension = useMemo(
    () =>
      Extension.create({
        name: "promptCaretHint",
        addProseMirrorPlugins() {
          const hintText = caretHintText.trim();
          if (!hintText) {
            return [];
          }

          return [
            new Plugin<boolean>({
              key: CARET_HINT_PLUGIN_KEY,
              state: {
                init: () => false,
                apply(tr, previousState) {
                  const meta = tr.getMeta(CARET_HINT_PLUGIN_KEY);
                  if (typeof meta === "boolean") {
                    return meta;
                  }
                  return previousState;
                },
              },
              props: {
                handleDOMEvents: {
                  focus: (view) => {
                    view.dispatch(view.state.tr.setMeta(CARET_HINT_PLUGIN_KEY, true));
                    return false;
                  },
                  blur: (view) => {
                    view.dispatch(view.state.tr.setMeta(CARET_HINT_PLUGIN_KEY, false));
                    return false;
                  },
                },
                decorations: (state) => {
                  const isFocused = CARET_HINT_PLUGIN_KEY.getState(state);
                  if (!isFocused || !canShowCaretHintAtSelection(state)) {
                    return null;
                  }

                  const docJson = state.doc.toJSON() as JSONContent;
                  if (!hasRenderableContent(docJson)) {
                    return null;
                  }

                  const widget = Decoration.widget(
                    state.selection.from,
                    () => {
                      const hint = document.createElement("span");
                      hint.className = "prompt-variable-caret-hint";
                      hint.textContent = ` ${hintText}`;
                      hint.setAttribute("aria-hidden", "true");
                      hint.setAttribute("contenteditable", "false");
                      return hint;
                    },
                    {
                      side: 1,
                      ignoreSelection: true,
                    },
                  );

                  return DecorationSet.create(state.doc, [widget]);
                },
              },
            }),
          ];
        },
      }),
    [caretHintText],
  );

  const placeholderExtension = useMemo(
    () =>
      Placeholder.configure({
        placeholder: placeholder || "",
      }),
    [placeholder],
  );

  const variableTokenInputRuleExtension = useMemo(
    () =>
      Extension.create({
        name: "variableTokenInputRule",
        addInputRules() {
          return [
            new InputRule({
              find: /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}$/,
              handler: ({ chain, range, match }) => {
                const variableName = String(match[1] ?? "").trim();
                const option = variableLookup.get(variableName);
                if (!option) {
                  return null;
                }

                chain()
                  .insertContentAt(range, {
                    type: "variableMention",
                    attrs: {
                      id: option.name,
                      label: option.label,
                    },
                  })
                  .command(() => {
                    if (customizableVariableSetRef.current.has(option.name)) {
                      onVariableMentionInsertRef.current?.(option.name);
                    }
                    return true;
                  })
                  .run();
              },
            }),
          ];
        },
      }),
    [variableLookup],
  );

  const editor = useEditor(
    {
      extensions: [
        Document,
        Paragraph,
        Text,
        HardBreak,
        History,
        placeholderExtension,
        variableMentionExtension,
        caretHintExtension,
        variableTokenInputRuleExtension,
      ],
      content: buildDocFromTemplate(value, variableLookup),
      editable: !disabled,
      editorProps: {
        attributes: {
          class: cn("prompt-variable-editor", showInsertProjectContextButton && "prompt-variable-editor--with-insert-context", className),
          role: "textbox",
          "aria-multiline": "true",
          ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
          ...(ariaLabelledBy ? { "aria-labelledby": ariaLabelledBy } : {}),
          ...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {}),
          ...(disabled ? { "aria-disabled": "true" } : {}),
          ...(id ? { id } : {}),
        },
        clipboardTextSerializer: (slice) => {
          return serializeSliceToTemplate(slice);
        },
        handlePaste: (_view, event) => {
          const pastedText = event.clipboardData?.getData("text/plain") || "";
          if (!pastedText || !pastedText.includes("{{")) {
            return false;
          }

          const inlineContent = parseTemplateTextToInlineContent(pastedText, variableLookup);
          if (!hasVariableMentionNode(inlineContent)) {
            return false;
          }

          const activeEditor = editorInstanceRef.current;
          if (!activeEditor) {
            return false;
          }

          event.preventDefault();
          activeEditor.chain().focus().insertContent(inlineContent).run();
          return true;
        },
        handleDOMEvents: {
          copy: (view, event) => {
            const clipboardData = event.clipboardData;
            if (!clipboardData) {
              return false;
            }

            const selectedText = view.state.selection.empty ? serializeDomSelectionToTemplate(view.dom.ownerDocument.getSelection()) : serializeSliceToTemplate(view.state.selection.content());
            if (!selectedText) {
              return false;
            }

            clipboardData.setData("text/plain", selectedText);
            event.preventDefault();
            return true;
          },
          cut: (view, event) => {
            const clipboardData = event.clipboardData;
            if (!clipboardData) {
              return false;
            }

            const selectedText = view.state.selection.empty ? serializeDomSelectionToTemplate(view.dom.ownerDocument.getSelection()) : serializeSliceToTemplate(view.state.selection.content());
            if (!selectedText) {
              return false;
            }

            clipboardData.setData("text/plain", selectedText);
            if (!view.state.selection.empty) {
              view.dispatch(view.state.tr.deleteSelection().scrollIntoView());
            }
            event.preventDefault();
            return true;
          },
          mousedown: (view, event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
              pendingMentionClickRef.current = null;
              return false;
            }

            const removeButton = target.closest(".prompt-variable-mention-remove") as HTMLElement | null;
            if (removeButton) {
              const mentionElement = removeButton.closest(".prompt-variable-mention") as HTMLElement | null;
              if (!mentionElement) {
                pendingMentionClickRef.current = null;
                return false;
              }

              try {
                const mentionPosition = view.posAtDOM(mentionElement, 0);
                const mentionNode = view.state.doc.nodeAt(mentionPosition);
                if (!mentionNode || mentionNode.type.name !== "variableMention") {
                  pendingMentionClickRef.current = null;
                  return false;
                }

                const mentionEnd = mentionPosition + mentionNode.nodeSize;
                const charAfterMention = view.state.doc.textBetween(mentionEnd, mentionEnd + 1, "", "");

                const transaction = view.state.tr.delete(mentionPosition, mentionEnd);
                if (charAfterMention === " ") {
                  transaction.delete(mentionPosition, mentionPosition + 1);
                }
                view.focus();
                view.dispatch(transaction.scrollIntoView());
                pendingMentionClickRef.current = null;
                event.preventDefault();
                return true;
              } catch (_error) {
                pendingMentionClickRef.current = null;
                return false;
              }
            }

            const configurableMention = target.closest(".prompt-variable-mention--configurable") as HTMLElement | null;
            if (!configurableMention) {
              pendingMentionClickRef.current = null;
              return false;
            }

            const variableName = configurableMention.getAttribute("data-variable-id");
            if (!variableName) {
              pendingMentionClickRef.current = null;
              return false;
            }

            pendingMentionClickRef.current = {
              variableName,
              startX: event.clientX,
              startY: event.clientY,
            };
            return false;
          },
          dragstart: (_view, event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
              return false;
            }
            const mention = target.closest(".prompt-variable-mention") as HTMLElement | null;
            if (!mention) {
              return false;
            }
            isDraggingVariableRef.current = true;
            mention.classList.add("prompt-variable-mention--dragging");
            draggedVariableElementRef.current = mention;
            return false;
          },
          dragend: () => {
            draggedVariableElementRef.current?.classList.remove("prompt-variable-mention--dragging");
            draggedVariableElementRef.current = null;
            requestAnimationFrame(() => {
              isDraggingVariableRef.current = false;
            });
            return false;
          },
          mouseover: (_view, event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
              return false;
            }
            const removeButton = target.closest(".prompt-variable-mention-remove") as HTMLElement | null;
            if (!removeButton) {
              return false;
            }
            const mentionElement = removeButton.closest(".prompt-variable-mention--configurable") as HTMLElement | null;
            mentionElement?.classList.add("prompt-variable-mention--remove-hover");
            return false;
          },
          mouseout: (_view, event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
              return false;
            }
            const removeButton = target.closest(".prompt-variable-mention-remove") as HTMLElement | null;
            if (!removeButton) {
              return false;
            }
            const nextTarget = event.relatedTarget;
            if (nextTarget instanceof Element && removeButton.contains(nextTarget)) {
              return false;
            }
            const mentionElement = removeButton.closest(".prompt-variable-mention--configurable") as HTMLElement | null;
            mentionElement?.classList.remove("prompt-variable-mention--remove-hover");
            return false;
          },
          mouseup: (_view, event) => {
            if (!onVariableMentionClickRef.current) {
              pendingMentionClickRef.current = null;
              return false;
            }

            const pendingClick = pendingMentionClickRef.current;
            pendingMentionClickRef.current = null;
            if (!pendingClick) {
              return false;
            }

            if (isDraggingVariableRef.current) {
              // sometimes the dragend event doesn't fire, so we clear here on the next mouseup event
              isDraggingVariableRef.current = false;
              draggedVariableElementRef.current?.classList.remove("prompt-variable-mention--dragging");
              draggedVariableElementRef.current = null;
              return false;
            }

            const deltaX = Math.abs(event.clientX - pendingClick.startX);
            const deltaY = Math.abs(event.clientY - pendingClick.startY);
            const isClickIntent = deltaX < 5 && deltaY < 5;
            if (!isClickIntent) {
              return false;
            }

            event.preventDefault();
            onVariableMentionClickRef.current(pendingClick.variableName);
            return true;
          },
        },
        handleKeyDown: (_view, event) => {
          const reactEvent = event as unknown as React.KeyboardEvent<HTMLTextAreaElement>;
          onKeyDownRef.current?.(reactEvent);
          return event.defaultPrevented;
        },
      },
      onUpdate: ({ editor: nextEditor }) => {
        // Avoid dispatching transient initialization updates into Formik.
        // We only need change events for user edits while the editor is focused.
        if (!nextEditor.isFocused) {
          return;
        }

        const serialized = serializeDocToTemplate(nextEditor.getJSON());
        if (serialized === editorValueRef.current) {
          return;
        }
        editorValueRef.current = serialized;
        onChangeRef.current?.(createSyntheticChangeEvent(nameRef.current, serialized));
      },
      onBlur: () => {
        onBlurRef.current?.(createSyntheticBlurEvent(nameRef.current, editorValueRef.current));
      },
    },
    [variableMentionExtension, variableLookup, caretHintExtension, placeholderExtension, variableTokenInputRuleExtension, showInsertProjectContextButton, ariaLabel, ariaLabelledBy, ariaDescribedBy, id, className],
  );
  editorInstanceRef.current = editor;

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
    if (disabled) {
      editor.view.dom.setAttribute("aria-disabled", "true");
    } else {
      editor.view.dom.removeAttribute("aria-disabled");
    }
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const currentValue = serializeDocToTemplate(editor.getJSON());
    if (currentValue === value) {
      editorValueRef.current = value;
      return;
    }

    editorValueRef.current = value;
    editor.commands.setContent(buildDocFromTemplate(value, variableLookup), false);
  }, [editor, value, variableLookup]);

  useEffect(() => {
    if (!editor) return;

    const mentionElements = editor.view.dom.querySelectorAll<HTMLElement>(".prompt-variable-mention");

    mentionElements.forEach((element) => {
      const variableName = element.getAttribute("data-variable-id") || "";
      const shouldShowIndicator = indicatorVariableSetRef.current.has(variableName);
      const indicatorElement = element.querySelector<HTMLElement>(".prompt-variable-mention-indicator");

      if (shouldShowIndicator && !indicatorElement) {
        const indicator = document.createElement("span");
        indicator.className = "prompt-variable-mention-indicator";
        indicator.setAttribute("aria-hidden", "true");
        element.appendChild(indicator);
        return;
      }

      if (!shouldShowIndicator && indicatorElement) {
        indicatorElement.remove();
      }
    });
  }, [editor, indicatorVariablesSignature]);

  // Preserve API compatibility: this remains drop-in for Formik fields
  // even though TipTap doesn't consume all textarea attributes directly.
  void unusedTextareaProps;

  const insertProjectContextTrigger = () => {
    if (!editor || disabled) return;
    const { from } = editor.state.selection;
    const characterBeforeCursor = from > 1 ? editor.state.doc.textBetween(from - 1, from, "\n", "\n") : "";
    const shouldAddLeadingSpace = characterBeforeCursor.length > 0 && !/\s/.test(characterBeforeCursor);

    editor
      .chain()
      .focus()
      .insertContent(shouldAddLeadingSpace ? " #" : "#")
      .run();
  };

  const handleInsertProjectContextClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    insertProjectContextTrigger();
  };

  const handleInsertProjectContextKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.stopPropagation();
    insertProjectContextTrigger();
  };

  return (
    <Container>
      {editor ? <EditorContent editor={editor} /> : null}
      {showInsertProjectContextButton && (
        <SudoTooltip
          text="Insert Project Context"
          position="top"
          triggerAs="span"
          triggerClassName="prompt-variable-insert-context-wrapper"
          hasArrow={false}
          tooltipClassName="mb-2"
        >
          <span
            role="button"
            tabIndex={disabled ? -1 : 0}
            className="prompt-variable-insert-context"
            aria-label="Open project context variables menu"
            data-disabled={disabled ? "true" : undefined}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={handleInsertProjectContextClick}
            onKeyDown={handleInsertProjectContextKeyDown}
          >
            #
          </span>
        </SudoTooltip>
      )}
    </Container>
  );
};
